import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useRewardDishOptions } from '../hooks/useRewardDishOptions'

/**
 * Add/edit form for one reward, rendered by DynamicForm inside a Modal, the way
 * every other record in this app is edited.
 */
export const useRewardFormConfig = () => {
  const { t } = useTranslation()
  const { dishOptions } = useRewardDishOptions()

  return {
    id: 'loyalty-reward-form',
    fields: [
      {
        /*
          A searchable multi-select rather than a plain list: the whole menu goes
          in here, and the shared Select is an Autocomplete, so it filters as you
          type and collapses the chosen chips to "first two + N".
        */
        name: 'dishes',
        label: t('LOYALTY.REWARDS.FORM.DISHES'),
        placeholder: t('LOYALTY.REWARDS.FORM.DISHES_PLACEHOLDER'),
        focused: true,
        type: 'multi-select',
        options: dishOptions,
        required: true,
        // The modal is 400px wide and dish names are long, so the chip strip's
        // sideways scroll left the second chip sliced by the field's edge —
        // it read as broken rather than as something to scroll. Same fix the
        // language picker needed in the narrow accordion column: the last chip
        // shrinks to an ellipsis instead, and keeps its remove icon.
        fitTags: true
      },
      {
        name: 'points',
        label: t('LOYALTY.REWARDS.FORM.POINTS'),
        focused: true,
        type: 'number',
        onlyInteger: true,
        required: true
      },
      {
        /*
          Stacked under the points rather than beside them: the modal is 400px
          wide and both labels are long enough that a two-column row would clip
          them.
        */
        name: 'maxPerRedemption',
        label: t('LOYALTY.REWARDS.FORM.LIMIT'),
        focused: true,
        type: 'number',
        onlyInteger: true,
        required: true
      }
    ],
    validationSchema: z.object({
      dishes: z
        .array(z.object({ label: z.string().optional(), value: z.string() }))
        .min(1, t('LOYALTY.REWARDS.FORM.DISHES_REQUIRED')),
      // Arrives from a text input, so it is a string here whatever the field
      // type says; coerce rather than reject the shape the form actually sends.
      points: z.coerce
        .number({ message: t('LOYALTY.REWARDS.FORM.POINTS_REQUIRED') })
        .int()
        .positive(t('LOYALTY.REWARDS.FORM.POINTS_POSITIVE')),
      // Same coercion for the same reason: the input hands over a string, and
      // an empty one has to fail as "at least one" rather than as a type error.
      maxPerRedemption: z.coerce
        .number({ message: t('LOYALTY.REWARDS.FORM.LIMIT_REQUIRED') })
        .int()
        .positive(t('LOYALTY.REWARDS.FORM.LIMIT_POSITIVE'))
    })
  }
}
